import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
    useEventGuests,
    useEventGuestStats,
    useCreateEventGuest,
    useUpdateEventGuest,
    useDeleteEventGuest,
    useAssignGuestRfid,
    useDeleteGuestRfid,
    useToggleGuestPrinted,
    useEventGuestPrintBatches,
} from "@/services/eventGuestService";
import type { EventGuest, EventGuestFilters } from "@/services/eventGuestService";
import {
    useCardCategories,
    useCreateCardCategory,
    useUpdateCardCategory,
    useDeleteCardCategory,
} from "@/services/cardCategoryService";
import type { CardCategory } from "@/services/cardCategoryService";
import { useAdminCertificateTypes } from "@/services/adminCertificateTypeService";
import { usePeriods } from "@/services/usePeriods";
import { useUser } from "@/context/useUser";
import { axiosClient } from "@/lib/axiosClient";
import ExportButton from "@/components/private/pic/ExportButton";
import EventGuestImportModal from "@/components/private/event-guests/EventGuestImportModal";
import ParticipantsPage from "@/pages/private/admin/participants/ParticipantsPage";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ITEMS_BLOOD_TYPE, ITEMS_CLOTHES_SIZE } from "@/constants/options";
import {
    Users2, Plus, Edit2, Trash2, Loader2, Upload, Search, IdCard,
    Settings2, Nfc, Image as ImageIcon, CheckCircle2, XCircle as XCircleIcon, Mail,
    FileArchive, Eye, MailCheck, ChevronDown, Printer, CreditCard, Phone, Building, X,
    MailWarning,
} from "lucide-react";

const formSchema = z.object({
    card_category_id: z.string().min(1, "Kategori wajib dipilih"),
    full_name: z.string().min(1, "Nama lengkap wajib diisi"),
    gender: z.string().optional().nullable(),
    email: z.string().email("Email tidak valid").optional().or(z.literal("")),
    phone_mobile: z.string().optional().nullable(),
    institution: z.string().optional().nullable(),
    position: z.string().optional().nullable(),
    certificate_role: z.string().optional().nullable(),
    blood_type: z.string().optional().nullable(),
    clothing_size: z.string().optional().nullable(),
    notes: z.string().optional().nullable(),
    is_active: z.boolean().default(true),
});
type FormValues = z.infer<typeof formSchema>;

const emptyDefaults: FormValues = {
    card_category_id: "",
    full_name: "",
    gender: "",
    email: "",
    phone_mobile: "",
    institution: "",
    position: "",
    certificate_role: "",
    blood_type: "",
    clothing_size: "",
    notes: "",
    is_active: true,
};

const emptyFilters: EventGuestFilters = {
    card_category_id: "",
    gender: "",
    blood_type: "",
    clothing_size: "",
    is_printed: "",
    is_emailed: "",
    rfid_status: "",
    print_batch: "",
    has_email: "",
};

export default function EventGuestsPage() {
    const queryClient = useQueryClient();
    const { data: currentUser } = useUser();
    // Petugas RFID (role 7) hanya boleh mendaftarkan RFID — tidak boleh export/download/tambah/edit/hapus/kirim email/import/kelola pengaturan.
    const isPetugasRfid = currentUser?.role_id === 7;
    const { data: periods = [] } = usePeriods();
    const [periodId, setPeriodId] = useState<string>(() => {
        const flaggedActive = periods.find((p) => p.is_active);
        return flaggedActive ? String(flaggedActive.period_id) : "";
    });

    useEffect(() => {
        if (!periodId && periods.length > 0) {
            const flaggedActive = periods.find((p) => p.is_active);
            setPeriodId(flaggedActive ? String(flaggedActive.period_id) : String(periods[0].period_id));
        }
    }, [periods, periodId]);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<EventGuestFilters>(emptyFilters);
    // "oldest" = urutan id ASC, SAMA PERSIS dengan urutan yang dipakai Export & Set Tercetak,
    // supaya baris ke-N di tabel = baris ke-N di file export saat memakai "Jumlah Data".
    const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");

    const updateFilter = (key: keyof EventGuestFilters, value: string) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(1);
    };
    const resetFilters = () => {
        setFilters(emptyFilters);
        setSearch("");
        setPage(1);
    };
    const isFiltering = Boolean(search) || Object.values(filters).some((v) => v);

    const activeFilters: EventGuestFilters = { ...filters, period_id: periodId || undefined };

    const { data: categories = [] } = useCardCategories(periodId);
    const { data: printBatches = [] } = useEventGuestPrintBatches(periodId);
    const { data: responseData, isLoading } = useEventGuests(page, search, perPage, activeFilters, sortOrder);
    const { data: stats } = useEventGuestStats(search, activeFilters);
    const guests: EventGuest[] = responseData?.data || [];
    const totalPages = responseData?.last_page || 1;
    const totalItems = stats?.total ?? responseData?.total ?? 0;
    const fromItem = responseData?.from || 0;
    const toItem = responseData?.to || 0;

    const createMutation = useCreateEventGuest();
    const updateMutation = useUpdateEventGuest();
    const deleteMutation = useDeleteEventGuest();
    const assignRfidMutation = useAssignGuestRfid();
    const togglePrintedMutation = useToggleGuestPrinted();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingGuest, setEditingGuest] = useState<EventGuest | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        title: string; description: string; actionText: string; action: () => void; isDestructive?: boolean;
    } | null>(null);

    const [isImportOpen, setIsImportOpen] = useState(false);
    const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
    const [rfidTarget, setRfidTarget] = useState<EventGuest | null>(null);
    const [rfidValue, setRfidValue] = useState("");
    const [detailGuest, setDetailGuest] = useState<EventGuest | null>(null);
    const [isDownloadingZip, setIsDownloadingZip] = useState(false);
    const [isDownloadingZipPdf, setIsDownloadingZipPdf] = useState(false);
    const [isDownloadingQrZip, setIsDownloadingQrZip] = useState(false);
    const [isBulkMarking, setIsBulkMarking] = useState(false);
    const [exportLimit, setExportLimit] = useState<string>("");
    const [hdMode, setHdMode] = useState(false);

    const isMutating = createMutation.isPending || updateMutation.isPending;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: emptyDefaults,
    });

    const handleOpenDialog = (guest?: EventGuest) => {
        setSelectedFile(null);
        if (guest) {
            setEditingGuest(guest);
            form.reset({
                card_category_id: String(guest.card_category_id),
                full_name: guest.full_name,
                gender: guest.gender || "",
                email: guest.email || "",
                phone_mobile: guest.phone_mobile || "",
                institution: guest.institution || "",
                position: guest.position || "",
                certificate_role: guest.certificate_role || "",
                blood_type: guest.blood_type || "",
                clothing_size: guest.clothing_size || "",
                notes: guest.notes || "",
                is_active: Boolean(guest.is_active),
            });
        } else {
            setEditingGuest(null);
            form.reset(emptyDefaults);
        }
        setIsDialogOpen(true);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]);
    };

    const onSubmit = (values: FormValues) => {
        if (!periodId) {
            toast.error("Pilih periode terlebih dahulu");
            return;
        }
        const formData = new FormData();
        formData.append("period_id", periodId);
        Object.entries(values).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                formData.append(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
            }
        });
        if (selectedFile) formData.append("photo", selectedFile);

        if (editingGuest) {
            // Route update event-guest adalah POST (bukan PUT) - lihat eventGuestService.ts.
            // JANGAN tambahkan _method=PUT, Laravel akan mencari route PUT yang tidak terdaftar.
            updateMutation.mutate(
                { id: editingGuest.id, payload: formData },
                {
                    onSuccess: () => { toast.success("Data berhasil diperbarui"); setIsDialogOpen(false); },
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal memperbarui data"),
                }
            );
        } else {
            createMutation.mutate(formData, {
                onSuccess: () => { toast.success("Data berhasil ditambahkan"); setIsDialogOpen(false); },
                onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menambahkan data"),
            });
        }
    };

    const handleDelete = (guest: EventGuest) => {
        setAlertConfig({
            title: "Hapus Data",
            description: `Apakah Anda yakin ingin menghapus "${guest.full_name}"? Tindakan ini tidak dapat dibatalkan.`,
            actionText: "Hapus",
            isDestructive: true,
            action: () => {
                deleteMutation.mutate(guest.id, {
                    onSuccess: () => toast.success("Data berhasil dihapus"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus data"),
                });
            },
        });
        setIsAlertOpen(true);
    };

    const handlePrintCard = async (guest: EventGuest) => {
        try {
            const res = await axiosClient.get(`/event-guests/${guest.id}/card-image`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "image/jpeg" }));
            window.open(url, "_blank");
            // Sengaja TIDAK mengubah status cetak di sini — melihat/mengunduh kartu
            // bukan bukti kartu sudah benar-benar dicetak fisik. Status cetak hanya
            // diubah manual oleh admin lewat tombol "Tandai Sudah Cetak".
        } catch {
            toast.error("Gagal memuat kartu");
        }
    };

    const handleSendCardEmail = async (guest: EventGuest) => {
        if (!guest.email) {
            toast.error("Data ini tidak memiliki alamat email");
            return;
        }
        const toastId = toast.loading("Mengirim email kartu...");
        try {
            const { data } = await axiosClient.post(`/event-guests/${guest.id}/send-card`);
            toast.dismiss(toastId);
            toast.success(data.message || "Email berhasil dikirim");
        } catch (error: any) {
            toast.dismiss(toastId);
            toast.error(error?.response?.data?.message || "Gagal mengirim email kartu");
        }
    };

    const handleDownloadZip = async () => {
        setIsDownloadingZip(true);
        const toastId = toast.loading("Menyiapkan ZIP kartu...");
        try {
            const params = new URLSearchParams(exportParamsWithLimit);
            if (hdMode) params.append("hd", "1");
            const res = await axiosClient.get(`/event-guests/export-zip?${params.toString()}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Kartu_Panitia_Tamu_${Date.now()}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.dismiss(toastId);
            toast.success("Berhasil mengunduh ZIP kartu");
            // Sengaja TIDAK mengubah status cetak — mengunduh ZIP bukan bukti kartu
            // sudah benar-benar dicetak fisik. Tandai manual lewat toggle di tabel/detail.
        } catch (error: any) {
            toast.dismiss(toastId);
            toast.error(error?.response?.data?.message || "Gagal mengunduh ZIP kartu");
        } finally {
            setIsDownloadingZip(false);
        }
    };

    const handleDownloadZipPdf = async (mode: "separate" | "combined") => {
        setIsDownloadingZipPdf(true);
        const toastId = toast.loading(mode === "combined" ? "Menyiapkan PDF gabungan..." : "Menyiapkan ZIP PDF kartu...");
        try {
            const params = new URLSearchParams(exportParamsWithLimit);
            if (hdMode) params.append("hd", "1");
            params.append("mode", mode);
            const res = await axiosClient.get(`/event-guests/export-zip-pdf?${params.toString()}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            const extension = mode === "combined" ? "pdf" : "zip";
            link.setAttribute("download", `Kartu_PDF_Panitia_Tamu_${Date.now()}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.dismiss(toastId);
            toast.success(mode === "combined" ? "File PDF gabungan berhasil diunduh!" : "Berhasil mengunduh ZIP PDF kartu");
        } catch (error: any) {
            toast.dismiss(toastId);
            toast.error(error?.response?.data?.message || "Gagal mengunduh PDF kartu");
        } finally {
            setIsDownloadingZipPdf(false);
        }
    };

    const handleDownloadQrZip = async () => {
        setIsDownloadingQrZip(true);
        const toastId = toast.loading("Menyiapkan ZIP QR code...");
        try {
            const res = await axiosClient.get(`/event-guests/export-qr-zip?${exportParamsWithLimit.toString()}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `QRCode_Panitia_Tamu_${Date.now()}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.dismiss(toastId);
            toast.success("Berhasil mengunduh ZIP QR code");
        } catch (error: any) {
            toast.dismiss(toastId);
            toast.error(error?.response?.data?.message || "Gagal mengunduh ZIP QR code");
        } finally {
            setIsDownloadingQrZip(false);
        }
    };

    const handleBulkMarkPrinted = () => {
        if (!periodId) return;
        if (filters.is_printed !== "0") {
            toast.error('Pilih "Status Cetak: Belum Dicetak" terlebih dahulu sebelum menandai gelombang baru, agar kartu yang sudah dicetak sebelumnya tidak ikut tertimpa.');
            return;
        }
        const limitNum = exportLimit && Number(exportLimit) > 0 ? Number(exportLimit) : null;
        const confirmMessage = limitNum && limitNum < totalItems
            ? `Tandai ${limitNum} data PERTAMA (dari total ${totalItems} data sesuai filter saat ini, urutan data terlama) sebagai SUDAH DICETAK pada gelombang baru? Sisanya (${totalItems - limitNum} data) bisa ditandai menyusul di gelombang berikutnya.`
            : `Tandai SEMUA data (${totalItems}) yang sesuai filter saat ini (belum dicetak) sebagai SUDAH DICETAK pada gelombang baru?`;

        setAlertConfig({
            title: "Tandai Sudah Dicetak",
            description: confirmMessage,
            actionText: "Ya, Tandai",
            isDestructive: false,
            action: async () => {
                setIsBulkMarking(true);
                try {
                    // Pakai exportParamsWithLimit supaya "Jumlah Data" yang sama dengan Export/ZIP juga
                    // membatasi jumlah kartu yang ditandai tercetak (mis. baru cetak fisik 500 dari 652).
                    const { data } = await axiosClient.put(`/event-guests/bulk-mark-printed?${exportParamsWithLimit.toString()}`);
                    toast.success(data.message || "Berhasil menandai status cetak.");
                    updateFilter("is_printed", "");
                    queryClient.invalidateQueries({ queryKey: ["event-guests"] });
                    queryClient.invalidateQueries({ queryKey: ["event-guests-stats"] });
                    // Supaya dropdown "Gelombang Cetak" langsung menampilkan gelombang baru tanpa refresh manual.
                    queryClient.invalidateQueries({ queryKey: ["event-guests-print-batches"] });
                } catch (error: any) {
                    toast.error(error?.response?.data?.message || "Gagal menandai status cetak massal.");
                } finally {
                    setIsBulkMarking(false);
                }
            },
        });
        setIsAlertOpen(true);
    };

    const handleTogglePrinted = (guest: EventGuest) => {
        togglePrintedMutation.mutate(guest.id, {
            onSuccess: (data) => toast.success(data.message || "Status cetak diperbarui"),
            onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal mengubah status cetak"),
        });
    };

    const handleAssignRfid = () => {
        if (!rfidTarget || !rfidValue.trim()) return;
        assignRfidMutation.mutate(
            { id: rfidTarget.id, rfidNumber: rfidValue.trim() },
            {
                onSuccess: () => { toast.success("RFID berhasil didaftarkan"); setRfidTarget(null); setRfidValue(""); },
                onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal mendaftarkan RFID"),
            }
        );
    };

    const currentPeriod = periods.find((p) => String(p.period_id) === periodId);

    const exportParams = new URLSearchParams();
    if (search) exportParams.set("search", search);
    Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) exportParams.set(key, String(value));
    });

    // Params dilengkapi "limit" (opsional) supaya Excel, kedua ZIP, DAN "Set Tercetak" (bulk-mark-printed)
    // sama-sama ambil/menandai N data pertama yang PERSIS SAMA (urutan id, tertua dulu) - berguna kalau
    // kartu fisik yang sudah dicetak hari itu belum semua (mis. baru 500 dari 652 data).
    const exportParamsWithLimit = new URLSearchParams(exportParams);
    if (exportLimit && Number(exportLimit) > 0) exportParamsWithLimit.set("limit", exportLimit);
    const exportEndpoint = `/event-guests/export?${exportParamsWithLimit.toString()}`;

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <IdCard className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Panitia & Tamu</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Kelola data panitia & tamu untuk keperluan kartu akses, terpisah dari peserta pendaftar.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Select value={periodId} onValueChange={(v) => { setPeriodId(v); setPage(1); }}>
                        <SelectTrigger className="h-11 rounded-xl w-[220px]"><SelectValue placeholder="Pilih Periode" /></SelectTrigger>
                        <SelectContent>
                            {periods.map((p) => (
                                <SelectItem key={p.period_id} value={String(p.period_id)}>
                                    {p.name}{p.is_active ? " (Aktif)" : ""}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <Tabs defaultValue="guests" className="w-full">
                <TabsList className="mb-4 bg-slate-100 dark:bg-slate-800/50 p-1.5 rounded-xl w-full sm:w-auto flex overflow-x-auto border border-slate-200 dark:border-slate-700 justify-start h-auto">
                    <TabsTrigger value="guests" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-6 py-2.5">Panitia & Tamu</TabsTrigger>
                    <TabsTrigger value="participants" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-6 py-2.5">Peserta (Lunas)</TabsTrigger>
                    <TabsTrigger value="print-queue" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-6 py-2.5">Cetak Massal (Hari-H)</TabsTrigger>
                </TabsList>

                <TabsContent value="guests" className="mt-0 outline-none focus:outline-none space-y-6">
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                            <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Users2 className="w-4 h-4 text-indigo-500" /> {isFiltering ? "Hasil Filter" : "Total"}</p>
                            <p className="text-3xl font-black text-slate-900 dark:text-white">{totalItems}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                            <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Sudah Dicetak</p>
                            <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">{stats?.printed ?? 0}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                            <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Belum Dicetak</p>
                            <p className="text-3xl font-black text-amber-600 dark:text-amber-400">{stats?.not_printed ?? 0}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                            <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Punya RFID</p>
                            <p className="text-3xl font-black text-sky-600 dark:text-sky-400">{stats?.with_rfid ?? 0}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                            <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Email Terkirim</p>
                            <p className="text-3xl font-black text-purple-600 dark:text-purple-400">{stats?.emailed ?? 0}</p>
                        </div>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={() => updateFilter("has_email", "0")}
                                    className="text-left bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] hover:border-rose-300 dark:hover:border-rose-800 transition-colors"
                                >
                                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                        <MailWarning className="w-4 h-4 text-rose-500" /> Belum Ada Email
                                    </p>
                                    <p className="text-3xl font-black text-rose-600 dark:text-rose-400">{stats?.no_email ?? 0}</p>
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top"><p>Data tamu yang kolom emailnya masih kosong (klik untuk filter). Kartu fisik & QR tetap bisa dicetak normal, hanya fitur kirim email &amp; akun login otomatis yang tidak aktif untuk data ini.</p></TooltipContent>
                        </Tooltip>
                    </div>

                    {/* Toolbar — dibungkus 1 card supaya rapi di layar sempit (14") */}
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 sm:p-5 space-y-4">
                        {/* Baris pencarian */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Cari nama, email, institusi, no. kartu..."
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                    className="w-full h-10 pl-11 pr-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium"
                                />
                            </div>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Select value={sortOrder} onValueChange={(v) => { setSortOrder(v as "newest" | "oldest"); setPage(1); }}>
                                        <SelectTrigger className="h-10 rounded-xl w-[170px] shrink-0"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="newest">Terbaru Dulu</SelectItem>
                                            <SelectItem value="oldest">Terlama Dulu</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </TooltipTrigger>
                                <TooltipContent side="top"><p>"Terlama Dulu" memakai urutan yang sama dengan Export & Set Tercetak, jadi baris ke-N di sini = baris ke-N di file export</p></TooltipContent>
                            </Tooltip>
                            {isFiltering && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button variant="outline" onClick={resetFilters} className="h-10 rounded-xl shrink-0">Reset Filter</Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top"><p>Kembalikan semua filter ke default</p></TooltipContent>
                                </Tooltip>
                            )}
                        </div>

                        {/* Baris filter — grid responsif supaya label tidak terpotong */}
                        <div>
                            <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Filter</p>
                            <div className="grid gap-2.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))" }}>
                                <Select value={filters.card_category_id ? String(filters.card_category_id) : "__all__"} onValueChange={(v) => updateFilter("card_category_id", v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Kategori" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">Semua Kategori</SelectItem>
                                        {categories.map((c) => (
                                            <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.gender || "__all__"} onValueChange={(v) => updateFilter("gender", v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Jenis Kelamin" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">Semua Gender</SelectItem>
                                        <SelectItem value="male">Laki-laki</SelectItem>
                                        <SelectItem value="female">Perempuan</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={filters.blood_type || "__all__"} onValueChange={(v) => updateFilter("blood_type", v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Gol. Darah" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">Semua Gol. Darah</SelectItem>
                                        {ITEMS_BLOOD_TYPE.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.clothing_size || "__all__"} onValueChange={(v) => updateFilter("clothing_size", v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Ukuran Baju" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">Semua Ukuran</SelectItem>
                                        {ITEMS_CLOTHES_SIZE.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.is_printed === "" || filters.is_printed === undefined ? "__all__" : filters.is_printed} onValueChange={(v) => updateFilter("is_printed", v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Status Cetak" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">Semua Status Cetak</SelectItem>
                                        <SelectItem value="1">Sudah Dicetak</SelectItem>
                                        <SelectItem value="0">Belum Dicetak</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={filters.print_batch || "__all__"} onValueChange={(v) => updateFilter("print_batch", v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Gelombang Cetak" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">Semua Gelombang</SelectItem>
                                        {printBatches.map((b) => (
                                            <SelectItem key={b.print_batch} value={String(b.print_batch)}>
                                                Gelombang {b.print_batch} ({b.total} kartu)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.rfid_status || "__all__"} onValueChange={(v) => updateFilter("rfid_status", v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Status RFID" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">Semua Status RFID</SelectItem>
                                        <SelectItem value="registered">Sudah Terdaftar</SelectItem>
                                        <SelectItem value="unregistered">Belum Terdaftar</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={filters.is_emailed === "" || filters.is_emailed === undefined ? "__all__" : filters.is_emailed} onValueChange={(v) => updateFilter("is_emailed", v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Status Email" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">Semua Status Email</SelectItem>
                                        <SelectItem value="1">Sudah Terkirim</SelectItem>
                                        <SelectItem value="0">Belum Terkirim</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="block w-full">
                                            <Select value={filters.has_email === "" || filters.has_email === undefined ? "__all__" : filters.has_email} onValueChange={(v) => updateFilter("has_email", v === "__all__" ? "" : v)}>
                                                <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Data Email" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__all__">Semua Data Email</SelectItem>
                                                    <SelectItem value="1">Punya Email</SelectItem>
                                                    <SelectItem value="0">Belum Ada Email</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top"><p>Beda dari "Status Email" (status kirim) - ini mengecek apakah kolom emailnya sudah diisi sama sekali atau masih kosong</p></TooltipContent>
                                </Tooltip>
                            </div>
                        </div>

                        {/* Baris aksi — dipisah dari filter dengan garis, kiri aksi sekunder, kanan aksi utama */}
                        <div className="border-t border-slate-100 dark:border-slate-800 pt-4 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                                {!isPetugasRfid && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="outline" size="sm" onClick={() => setIsCategoryDialogOpen(true)} className="h-9 rounded-xl">
                                                <Settings2 className="w-4 h-4 mr-1.5" /> Kelola Kategori
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top"><p>Tambah/ubah kategori kartu (VVIP, Panitia, dll)</p></TooltipContent>
                                    </Tooltip>
                                )}
                                {!isPetugasRfid && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="outline" size="sm" onClick={() => setIsImportOpen(true)} className="h-9 rounded-xl">
                                                <Upload className="w-4 h-4 mr-1.5" /> Import Excel
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top"><p>Import data panitia/tamu massal dari file Excel</p></TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {!isPetugasRfid && (
                                    <>
                                        <input
                                            type="number"
                                            min={1}
                                            placeholder="Jumlah Data"
                                            value={exportLimit}
                                            onChange={(e) => setExportLimit(e.target.value)}
                                            title="Batasi jumlah data yang diambil Excel, ZIP Kartu, ZIP QR, DAN ditandai oleh 'Set Tercetak' (kosongkan untuk semua data sesuai filter)"
                                            className="w-[110px] h-9 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none shadow-sm font-medium"
                                        />
                                        <label
                                            className="flex items-center gap-1.5 h-9 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm text-sm font-semibold text-slate-600 dark:text-slate-300 cursor-pointer select-none"
                                            title="Render kartu di resolusi 2x (QR & teks lebih tajam) - dipakai untuk kebutuhan cetak fisik"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={hdMode}
                                                onChange={(e) => setHdMode(e.target.checked)}
                                                className="w-4 h-4 accent-amber-600"
                                            />
                                            HD
                                        </label>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span>
                                                    <ExportButton
                                                        endpoint={exportEndpoint}
                                                        filenamePrefix="panitia-tamu"
                                                        className="h-9 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 hover:border-emerald-700 dark:hover:bg-emerald-700 dark:hover:text-white font-bold text-sm shadow-sm min-w-0"
                                                    />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="top"><p>Export data ke Excel sesuai filter aktif</p></TooltipContent>
                                        </Tooltip>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    size="sm"
                                                    disabled={isDownloadingZip || isDownloadingZipPdf || isDownloadingQrZip}
                                                    className="h-9 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold shadow-sm"
                                                >
                                                    {(isDownloadingZip || isDownloadingZipPdf || isDownloadingQrZip) ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileArchive className="w-4 h-4 mr-1.5" />}
                                                    Download ZIP
                                                    <ChevronDown className="w-4 h-4 ml-1.5" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-[260px] rounded-xl z-[99] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl">
                                                <DropdownMenuLabel className="text-xs text-slate-500">Unduh ZIP</DropdownMenuLabel>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={handleDownloadZip} disabled={isDownloadingZip}>
                                                    Kartu Lengkap - JPG
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleDownloadZipPdf("separate")} disabled={isDownloadingZipPdf}>
                                                    Kartu Lengkap - PDF (Banyak File)
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleDownloadZipPdf("combined")} disabled={isDownloadingZipPdf}>
                                                    Kartu Lengkap - PDF (1 File Gabungan)
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={handleDownloadQrZip} disabled={isDownloadingQrZip}>
                                                    QR Code Saja (untuk Bank)
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    size="sm"
                                                    onClick={handleBulkMarkPrinted}
                                                    disabled={isBulkMarking || !periodId}
                                                    className="h-9 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-sm"
                                                >
                                                    {isBulkMarking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                                                    Set Tercetak
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top"><p>Tandai data yang cocok filter saat ini sebagai sudah dicetak sekaligus (isi "Jumlah Data" untuk menandai sebagian dulu, mis. 500 dari 652)</p></TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button size="sm" onClick={() => handleOpenDialog()} className="h-9 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-sm">
                                                    <Plus className="w-4 h-4 mr-1.5" /> Tambah
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top"><p>Tambah data panitia/tamu baru</p></TooltipContent>
                                        </Tooltip>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                        <div className="overflow-x-auto">
                            {isLoading ? (
                                <div className="flex items-center justify-center h-48">
                                    <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                                </div>
                            ) : (
                                <table className="w-full text-left text-sm whitespace-nowrap">
                                    <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                                        <tr>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 w-[50px] text-center">No</th>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Nama</th>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Kategori</th>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Institusi</th>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">No. Kartu / RFID</th>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Cetak</th>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Email</th>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Status</th>
                                            <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {guests.length === 0 ? (
                                            <tr><td colSpan={9} className="p-12 text-center text-slate-500">Belum ada data.</td></tr>
                                        ) : guests.map((guest, index) => (
                                            <tr key={guest.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                                                <td className="px-6 py-4 text-center font-medium">{(page - 1) * perPage + index + 1}</td>
                                                <td className="px-6 py-4 font-semibold text-slate-900 dark:text-slate-100">
                                                    {guest.full_name}
                                                    {guest.email ? (
                                                        <div className="text-[11px] font-normal text-slate-400">{guest.email}</div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <div className="text-[11px] font-bold text-rose-500 dark:text-rose-400 flex items-center gap-1 w-fit">
                                                                        <MailWarning className="w-3 h-3" /> Belum ada email
                                                                    </div>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top"><p>Kartu fisik &amp; QR tetap bisa dicetak normal - hanya kirim email &amp; akun login otomatis yang tidak aktif untuk data ini</p></TooltipContent>
                                                            </Tooltip>
                                                            {guest.phone_mobile && (
                                                                <span className="text-[11px] font-normal text-slate-400">· {guest.phone_mobile}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold"
                                                        style={{
                                                            backgroundColor: (guest.card_category?.color_hex || "#6366F1") + "20",
                                                            color: guest.card_category?.color_hex || "#6366F1",
                                                        }}
                                                    >
                                                        {guest.card_category?.name || "-"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{guest.institution || "-"}</td>
                                                <td className="px-6 py-4">
                                                    <div className="font-mono text-xs">{guest.card_number}</div>
                                                    <button
                                                        onClick={() => { setRfidTarget(guest); setRfidValue(guest.rfid_number || ""); }}
                                                        className="text-[11px] mt-1 flex items-center gap-1 text-indigo-600 hover:underline"
                                                    >
                                                        <Nfc className="w-3 h-3" /> {guest.rfid_number || "Assign RFID"}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button onClick={() => handleTogglePrinted(guest)} className="inline-flex">
                                                                    {guest.is_printed ? (
                                                                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                                                    ) : (
                                                                        <XCircleIcon className="w-4 h-4 text-slate-300" />
                                                                    )}
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top">
                                                                <p>{guest.is_printed ? "Sudah dicetak — klik untuk batal tandai" : "Belum dicetak — klik untuk tandai sudah cetak"}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                        {guest.is_printed && guest.print_batch != null && (
                                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 whitespace-nowrap">
                                                                Gel. {guest.print_batch}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="inline-flex">
                                                                {guest.is_emailed ? (
                                                                    <MailCheck className="w-4 h-4 text-sky-500" />
                                                                ) : (
                                                                    <Mail className="w-4 h-4 text-slate-300" />
                                                                )}
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top">
                                                            <p>{guest.is_emailed ? "Email kartu sudah dikirim" : "Email kartu belum dikirim"}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {guest.is_active ? (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                                            Aktif
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
                                                            Nonaktif
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-right sticky right-0 z-10 bg-white dark:bg-slate-900">
                                                    <div className="flex justify-end gap-2">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button variant="outline" size="icon" className="h-8 w-8 text-slate-600" onClick={() => setDetailGuest(guest)}>
                                                                    <Eye className="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top"><p>Lihat Detail</p></TooltipContent>
                                                        </Tooltip>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button variant="outline" size="icon" className="h-8 w-8 text-slate-600" onClick={() => handlePrintCard(guest)}>
                                                                    <ImageIcon className="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top"><p>Cetak Kartu</p></TooltipContent>
                                                        </Tooltip>
                                                        {!isPetugasRfid && guest.email && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="outline" size="icon" className="h-8 w-8 text-sky-600" onClick={() => handleSendCardEmail(guest)}>
                                                                        <Mail className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top"><p>Kirim Email Kartu</p></TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                        {!isPetugasRfid && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="outline" size="icon" className="h-8 w-8 text-indigo-600" onClick={() => handleOpenDialog(guest)}>
                                                                        <Edit2 className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top"><p>Edit Data</p></TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                        {!isPetugasRfid && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="outline" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(guest)}>
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top"><p>Hapus</p></TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        {totalItems > 0 && (
                            <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                                <div className="flex items-center gap-3 text-slate-500 font-medium">
                                    <span>Menampilkan {fromItem} - {toItem} dari {totalItems} data</span>
                                    <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block" />
                                    <div className="flex items-center gap-2">
                                        <label htmlFor="eventGuestPerPage">Tampilkan:</label>
                                        <select
                                            id="eventGuestPerPage"
                                            value={perPage}
                                            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-primary font-bold"
                                        >
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg">Sebelumnya</Button>
                                    <span className="px-2 font-bold text-slate-700 dark:text-slate-300">{page} / {totalPages}</span>
                                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-lg">Selanjutnya</Button>
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="participants" className="mt-0 outline-none focus:outline-none">
                    <ParticipantsPage defaultFilters={{ payment_status: 'paid' }} hideHeader={true} />
                </TabsContent>

                <TabsContent value="print-queue" className="mt-0 outline-none focus:outline-none">
                    <PrintQueueTab periodId={periodId} />
                </TabsContent>
            </Tabs>

            {/* Dialog Tambah/Edit Guest */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingGuest ? "Edit Data" : "Tambah Panitia/Tamu"}</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField control={form.control} name="card_category_id" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Kategori Kartu <span className="text-red-500">*</span></FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl><SelectTrigger><SelectValue placeholder="Pilih Kategori" /></SelectTrigger></FormControl>
                                            <SelectContent>
                                                {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="full_name" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nama Lengkap <span className="text-red-500">*</span></FormLabel>
                                        <FormControl><Input placeholder="Nama lengkap" {...field} /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="gender" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Jenis Kelamin</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value || ""}>
                                            <FormControl><SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger></FormControl>
                                            <SelectContent>
                                                <SelectItem value="male">Laki-laki</SelectItem>
                                                <SelectItem value="female">Perempuan</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="email" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl><Input type="email" placeholder="email@contoh.com" {...field} /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="phone_mobile" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>No. HP/WA</FormLabel>
                                        <FormControl><Input {...field} value={field.value || ""} /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="institution" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Institusi</FormLabel>
                                        <FormControl><Input {...field} value={field.value || ""} /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="position" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Jabatan</FormLabel>
                                        <FormControl><Input {...field} value={field.value || ""} /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="certificate_role" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Peran di Sertifikat</FormLabel>
                                        <FormControl><Input placeholder="mis. sebagai Panitia Pelaksana" {...field} value={field.value || ""} /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="blood_type" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Golongan Darah</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value || ""}>
                                            <FormControl><SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger></FormControl>
                                            <SelectContent>
                                                {ITEMS_BLOOD_TYPE.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="clothing_size" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Ukuran Baju</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value || ""}>
                                            <FormControl><SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger></FormControl>
                                            <SelectContent>
                                                {ITEMS_CLOTHES_SIZE.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                            </div>
                            <FormField control={form.control} name="notes" render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Catatan</FormLabel>
                                    <FormControl><Textarea className="resize-none" {...field} value={field.value || ""} /></FormControl>
                                    <FormMessage />
                                </FormItem>
                            )} />
                            <FormField control={form.control} name="is_active" render={({ field }) => (
                                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <FormLabel className="text-base">Status Aktif</FormLabel>
                                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                                </FormItem>
                            )} />
                            <FormItem>
                                <FormLabel>Foto (Opsional)</FormLabel>
                                <input type="file" accept="image/*" onChange={handleFileChange} className="block text-sm" />
                                {selectedFile && <p className="text-xs text-slate-500 mt-1">{selectedFile.name}</p>}
                            </FormItem>
                            <div className="flex gap-3 pt-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl h-11 px-6">Batal</Button>
                                <Button type="submit" disabled={isMutating} className="rounded-xl h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                                    {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            {/* Dialog Assign RFID */}
            <Dialog open={!!rfidTarget} onOpenChange={(open) => !open && setRfidTarget(null)}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader><DialogTitle>Assign RFID — {rfidTarget?.full_name}</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-2">
                        <Input placeholder="Scan / ketik nomor RFID" value={rfidValue} onChange={(e) => setRfidValue(e.target.value)} />
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setRfidTarget(null)}>Batal</Button>
                            <Button onClick={handleAssignRfid} disabled={assignRfidMutation.isPending} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                {assignRfidMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog Detail */}
            <GuestDetailDialog
                guest={detailGuest}
                onClose={() => setDetailGuest(null)}
                onEdit={(g) => { setDetailGuest(null); handleOpenDialog(g); }}
                canEdit={!isPetugasRfid}
            />

            {/* Dialog Kelola Kategori */}
            <CardCategoryManagerDialog
                open={isCategoryDialogOpen}
                onOpenChange={setIsCategoryDialogOpen}
                periodId={periodId}
                categories={categories}
            />

            {/* Modal Import */}
            <EventGuestImportModal
                isOpen={isImportOpen}
                onClose={() => setIsImportOpen(false)}
                onImported={() => {
                    queryClient.invalidateQueries({ queryKey: ["event-guests"] });
                    queryClient.invalidateQueries({ queryKey: ["event-guests-stats"] });
                }}
                periodId={periodId}
                periodName={currentPeriod?.name}
                categories={categories}
            />

            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{alertConfig?.title}</AlertDialogTitle>
                        <AlertDialogDescription>{alertConfig?.description}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => { alertConfig?.action(); setIsAlertOpen(false); }}
                            className={alertConfig?.isDestructive ? "bg-rose-600 hover:bg-rose-700" : ""}
                        >
                            {alertConfig?.actionText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function CardCategoryManagerDialog({
    open, onOpenChange, periodId, categories,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    periodId: string;
    categories: CardCategory[];
}) {
    const createMutation = useCreateCardCategory();
    const updateMutation = useUpdateCardCategory();
    const deleteMutation = useDeleteCardCategory();

    const { data: certificateTypes } = useAdminCertificateTypes();
    const eventGuestCertTypes = certificateTypes?.filter((ct) => ct.source_type === "event_guest") ?? [];

    const [name, setName] = useState("");
    const [colorHex, setColorHex] = useState("#6366F1");
    const [certificateTypeId, setCertificateTypeId] = useState<string>("none");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingCategory, setEditingCategory] = useState<CardCategory | null>(null);
    const [frontFile, setFrontFile] = useState<File | null>(null);
    const [backFile, setBackFile] = useState<File | null>(null);
    const [layoutOverrides, setLayoutOverrides] = useState({
        qr_x: "", qr_y: "", qr_size: "", text_x: "", text_zone_top: "", text_zone_bottom: "",
    });

    const resetForm = () => {
        setName(""); setColorHex("#6366F1"); setCertificateTypeId("none"); setEditingId(null);
        setEditingCategory(null); setFrontFile(null); setBackFile(null);
        setLayoutOverrides({ qr_x: "", qr_y: "", qr_size: "", text_x: "", text_zone_top: "", text_zone_bottom: "" });
    };

    const handleEdit = (cat: CardCategory) => {
        setEditingId(cat.id);
        setEditingCategory(cat);
        setName(cat.name);
        setColorHex(cat.color_hex || "#6366F1");
        setCertificateTypeId(cat.certificate_type_id ? String(cat.certificate_type_id) : "none");
        setFrontFile(null);
        setBackFile(null);
        setLayoutOverrides({
            qr_x: cat.qr_x?.toString() ?? "",
            qr_y: cat.qr_y?.toString() ?? "",
            qr_size: cat.qr_size?.toString() ?? "",
            text_x: cat.text_x?.toString() ?? "",
            text_zone_top: cat.text_zone_top?.toString() ?? "",
            text_zone_bottom: cat.text_zone_bottom?.toString() ?? "",
        });
    };

    const handleSubmit = () => {
        if (!name.trim() || !periodId) return;
        const formData = new FormData();
        formData.append("period_id", periodId);
        formData.append("name", name.trim());
        formData.append("color_hex", colorHex);
        const hasCertType = certificateTypeId !== "none";
        formData.append("is_certificate_eligible", hasCertType ? "1" : "0");
        formData.append("certificate_type_id", hasCertType ? certificateTypeId : "");
        if (frontFile) formData.append("card_background_front", frontFile);
        if (backFile) formData.append("card_background_back", backFile);
        (Object.keys(layoutOverrides) as (keyof typeof layoutOverrides)[]).forEach((key) => {
            const raw = layoutOverrides[key];
            if (raw !== "" && !isNaN(Number(raw))) formData.append(key, raw);
        });

        if (editingId) {
            // Route update kategori adalah POST (bukan PUT) - lihat cardCategoryService.ts.
            // JANGAN tambahkan _method=PUT di sini, nanti Laravel malah cari route PUT yang tidak
            // terdaftar dan error "Method Not Allowed" (pernah kejadian, ini baru diperbaiki).
            updateMutation.mutate({ id: editingId, payload: formData }, {
                onSuccess: () => { toast.success("Kategori diperbarui"); resetForm(); },
                onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal memperbarui kategori"),
            });
        } else {
            createMutation.mutate(formData, {
                onSuccess: () => { toast.success("Kategori ditambahkan"); resetForm(); },
                onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menambahkan kategori"),
            });
        }
    };

    const handleDelete = (id: number) => {
        deleteMutation.mutate(id, {
            onSuccess: () => toast.success("Kategori dihapus"),
            onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus kategori"),
        });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetForm(); }}>
            <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Kelola Kategori Kartu</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-2">
                    <div className="flex gap-2 items-end">
                        <div className="flex-1">
                            <label className="text-xs font-bold text-slate-500 block mb-1">Nama Kategori</label>
                            <Input placeholder="mis. VVIP, Panitia" value={name} onChange={(e) => setName(e.target.value)} />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Warna</label>
                            <input type="color" value={colorHex} onChange={(e) => setColorHex(e.target.value)} className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-500 block mb-1">Tipe Sertifikat</label>
                        <Select value={certificateTypeId} onValueChange={setCertificateTypeId}>
                            <SelectTrigger className="h-10">
                                <SelectValue placeholder="Tidak berhak sertifikat" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Tidak berhak sertifikat</SelectItem>
                                {eventGuestCertTypes.map((ct) => (
                                    <SelectItem key={ct.id} value={String(ct.id)}>{ct.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-[10px] text-slate-400 italic mt-1">
                            Tamu di kategori ini akan mendapat sertifikat sesuai tipe yang dipilih. Tipe sertifikat dikelola di menu Tipe Sertifikat (kategori sumber data "Panitia & Tamu Konvensi").
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Desain Kartu Depan (Opsional)</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setFrontFile(e.target.files?.[0] || null)}
                                className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-600 file:text-xs file:font-bold"
                            />
                            {(frontFile || editingCategory?.card_background_front_url) && (
                                <img
                                    src={frontFile ? URL.createObjectURL(frontFile) : editingCategory?.card_background_front_url || ""}
                                    alt="Preview depan"
                                    className="mt-2 h-20 w-auto rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                                />
                            )}
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Desain Kartu Belakang (Opsional)</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setBackFile(e.target.files?.[0] || null)}
                                className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-600 file:text-xs file:font-bold"
                            />
                            {(backFile || editingCategory?.card_background_back_url) && (
                                <img
                                    src={backFile ? URL.createObjectURL(backFile) : editingCategory?.card_background_back_url || ""}
                                    alt="Preview belakang"
                                    className="mt-2 h-20 w-auto rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                                />
                            )}
                        </div>
                    </div>
                    <p className="text-[10px] text-slate-400 italic">Kosongkan jika ingin memakai desain kartu bawaan sistem.</p>

                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-500 block">Posisi QR & Teks (Opsional)</label>
                        <p className="text-[10px] text-slate-400 italic">
                            Kosongkan untuk pakai posisi bawaan sistem. Isi kalau background di atas desainnya beda sehingga
                            QR/teks perlu digeser. Koordinat berbasis kanvas 957x1524px.
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">QR - X (90)</label>
                                <Input type="number" placeholder="90" value={layoutOverrides.qr_x}
                                    onChange={(e) => setLayoutOverrides((v) => ({ ...v, qr_x: e.target.value }))} className="h-8 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">QR - Y (500)</label>
                                <Input type="number" placeholder="500" value={layoutOverrides.qr_y}
                                    onChange={(e) => setLayoutOverrides((v) => ({ ...v, qr_y: e.target.value }))} className="h-8 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">QR - Ukuran (330)</label>
                                <Input type="number" placeholder="330" value={layoutOverrides.qr_size}
                                    onChange={(e) => setLayoutOverrides((v) => ({ ...v, qr_size: e.target.value }))} className="h-8 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">Teks - X (90)</label>
                                <Input type="number" placeholder="90" value={layoutOverrides.text_x}
                                    onChange={(e) => setLayoutOverrides((v) => ({ ...v, text_x: e.target.value }))} className="h-8 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">Zona Atas (860)</label>
                                <Input type="number" placeholder="860" value={layoutOverrides.text_zone_top}
                                    onChange={(e) => setLayoutOverrides((v) => ({ ...v, text_zone_top: e.target.value }))} className="h-8 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">Zona Bawah (1180)</label>
                                <Input type="number" placeholder="1180" value={layoutOverrides.text_zone_bottom}
                                    onChange={(e) => setLayoutOverrides((v) => ({ ...v, text_zone_bottom: e.target.value }))} className="h-8 text-xs" />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        {editingId && <Button variant="outline" onClick={resetForm}>Batal Edit</Button>}
                        <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            {editingId ? "Simpan Perubahan" : "Tambah Kategori"}
                        </Button>
                    </div>

                    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-2 max-h-[300px] overflow-y-auto">
                        {categories.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Belum ada kategori.</p>}
                        {categories.map((cat) => (
                            <div key={cat.id} className="flex items-center justify-between p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-2">
                                    {cat.card_background_front_url ? (
                                        <img src={cat.card_background_front_url} alt={cat.name} className="w-8 h-8 rounded-md object-cover border border-slate-200 dark:border-slate-700" />
                                    ) : (
                                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color_hex || "#6366F1" }} />
                                    )}
                                    <span className="font-semibold text-sm">{cat.name}</span>
                                    {cat.certificate_type_id && (
                                        <span className="text-[10px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-full font-bold">
                                            {cat.certificate_type?.name || "Sertifikat"}
                                        </span>
                                    )}
                                </div>
                                <div className="flex gap-1">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(cat)}><Edit2 className="w-3.5 h-3.5" /></Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top"><p>Edit kategori</p></TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => handleDelete(cat.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top"><p>Hapus kategori</p></TooltipContent>
                                    </Tooltip>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function printImage(url: string, title: string) {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
        printWindow.document.write(`
            <html>
                <head>
                    <title>${title}</title>
                    <style>
                        body, html { margin: 0; padding: 0; width: 100%; height: 100%; background-color: #fff; }
                        img { width: 100vw; height: 100vh; object-fit: contain; display: block; margin: 0 auto; }
                        @media print {
                            @page { margin: 0; }
                            body, html { margin: 0; padding: 0; width: 100%; height: 100%; }
                            img { width: 100%; height: 100%; object-fit: fill; }
                        }
                    </style>
                </head>
                <body>
                    <img src="${url}" onload="window.print(); window.onafterprint = function() { window.close(); }" />
                </body>
            </html>
        `);
        printWindow.document.close();
    } else {
        toast.error("Pop-up diblokir oleh browser. Izinkan pop-up untuk mencetak.");
    }
}

function GuestDetailDialog({
    guest, onClose, onEdit, canEdit = true,
}: {
    guest: EventGuest | null;
    onClose: () => void;
    onEdit: (guest: EventGuest) => void;
    canEdit?: boolean;
}) {
    const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
    const [cardBackImageUrl, setCardBackImageUrl] = useState<string | null>(null);
    const [isLoadingCard, setIsLoadingCard] = useState(false);
    const [localIsPrinted, setLocalIsPrinted] = useState(false);
    const [rfidCodeForModal, setRfidCodeForModal] = useState("");
    const [isDeleteRfidDialogOpen, setIsDeleteRfidDialogOpen] = useState(false);
    const togglePrintedMutation = useToggleGuestPrinted();
    const assignRfidMutation = useAssignGuestRfid();
    const deleteRfidMutation = useDeleteGuestRfid();

    useEffect(() => {
        setLocalIsPrinted(Boolean(guest?.is_printed));
        setRfidCodeForModal("");
        setIsDeleteRfidDialogOpen(false);
    }, [guest]);

    const handleAssignRfidFromDetail = () => {
        if (!guest || !rfidCodeForModal.trim()) return;
        assignRfidMutation.mutate(
            { id: guest.id, rfidNumber: rfidCodeForModal.trim() },
            {
                onSuccess: () => { toast.success("RFID berhasil didaftarkan"); setRfidCodeForModal(""); },
                onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal mendaftarkan RFID"),
            }
        );
    };

    const handleDeleteRfidFromDetail = () => {
        setIsDeleteRfidDialogOpen(true);
    };

    const confirmDeleteRfid = () => {
        if (!guest) return;
        deleteRfidMutation.mutate(guest.id, {
            onSuccess: () => { 
                toast.success("RFID berhasil dihapus");
                setIsDeleteRfidDialogOpen(false);
            },
            onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus RFID"),
        });
    };

    const handleToggleFromDetail = () => {
        if (!guest) return;
        togglePrintedMutation.mutate(guest.id, {
            onSuccess: (res) => {
                toast.success(res.message || "Status cetak diperbarui");
                setLocalIsPrinted(Boolean(res.data?.is_printed));
            },
            onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal mengubah status cetak"),
        });
    };

    useEffect(() => {
        if (!guest) {
            setCardImageUrl(null);
            setCardBackImageUrl(null);
            return;
        }
        let isMounted = true;
        setIsLoadingCard(true);
        Promise.all([
            axiosClient.get(`/event-guests/${guest.id}/card-image`, { responseType: "blob" }),
            axiosClient.get(`/event-guests/${guest.id}/card-back-image`, { responseType: "blob" }),
        ])
            .then(([frontRes, backRes]) => {
                if (!isMounted) return;
                setCardImageUrl(window.URL.createObjectURL(new Blob([frontRes.data], { type: "image/jpeg" })));
                setCardBackImageUrl(window.URL.createObjectURL(new Blob([backRes.data], { type: "image/png" })));
                // Sengaja TIDAK mengubah status cetak di sini — melihat preview bukan
                // bukti kartu sudah benar-benar dicetak fisik.
            })
            .catch(() => { if (isMounted) { setCardImageUrl(null); setCardBackImageUrl(null); } })
            .finally(() => { if (isMounted) setIsLoadingCard(false); });
        return () => { isMounted = false; };
    }, [guest]);

    useEffect(() => {
        return () => {
            if (cardImageUrl) window.URL.revokeObjectURL(cardImageUrl);
            if (cardBackImageUrl) window.URL.revokeObjectURL(cardBackImageUrl);
        };
    }, [cardImageUrl, cardBackImageUrl]);

    const handlePrintFront = () => {
        if (!cardImageUrl || !guest) return;
        printImage(cardImageUrl, `Kartu_${guest.card_number}_${guest.full_name.replace(/\s+/g, "_")}_Depan`);
        toast.success("Membuka dialog cetak kartu (Depan)...");
    };

    const handlePrintBack = () => {
        if (!cardBackImageUrl || !guest) return;
        printImage(cardBackImageUrl, `Kartu_${guest.card_number}_${guest.full_name.replace(/\s+/g, "_")}_Belakang`);
        toast.success("Membuka dialog cetak kartu (Belakang)...");
    };

    const genderLabel = guest?.gender === "male" ? "Laki-laki" : guest?.gender === "female" ? "Perempuan" : "-";

    return (
        <Dialog open={!!guest} onOpenChange={(open) => !open && onClose()}>
            <DialogContent showCloseButton={false} className="!max-w-[90vw] sm:!max-w-[900px] w-full max-h-[92vh] overflow-hidden flex flex-col rounded-[2rem] p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
                <DialogHeader className="shrink-0 p-5 sm:p-6 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-left">
                    <div className="flex items-start gap-3.5 min-w-0 pr-8">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-200/60 dark:border-indigo-800/60 shadow-sm">
                            <IdCard className="w-7 h-7" />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-tight truncate">
                                {guest?.full_name || "-"}
                            </DialogTitle>
                            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-1.5 flex-wrap">
                                <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
                                    style={{
                                        backgroundColor: (guest?.card_category?.color_hex || "#6366F1") + "20",
                                        color: guest?.card_category?.color_hex || "#6366F1",
                                    }}
                                >
                                    {guest?.card_category?.name || "-"}
                                </span>
                                {guest?.is_emailed ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-sky-600"><MailCheck className="w-3 h-3" /> Email Terkirim</span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-400"><Mail className="w-3 h-3" /> Email Belum Terkirim</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogClose className="absolute right-6 top-6 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 dark:ring-offset-slate-950 dark:focus:ring-slate-300 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400">
                        <X className="h-5 w-5" />
                        <span className="sr-only">Close</span>
                    </DialogClose>
                </DialogHeader>
                {guest && (
                    <div className="flex-1 overflow-y-auto overflow-x-hidden p-5 sm:p-8 flex flex-col lg:flex-row gap-8 items-start w-full min-h-0">
                        <div className="w-full lg:w-[360px] shrink-0 space-y-4 mx-auto lg:mx-0 overflow-y-auto max-h-full pr-2 pb-4 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
                            <Tabs defaultValue="front" className="w-full">
                                <TabsList className="grid w-full grid-cols-2 mb-4 h-11 bg-slate-100 dark:bg-slate-800/80 rounded-xl p-1">
                                    <TabsTrigger value="front" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-sm font-bold">Depan</TabsTrigger>
                                    <TabsTrigger value="back" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-sm font-bold">Belakang</TabsTrigger>
                                </TabsList>

                                <TabsContent value="front" className="space-y-4 mt-0 border-0 p-0 focus-visible:ring-0">
                                    <div className="p-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-[2rem] shadow-xl">
                                        {isLoadingCard ? (
                                            <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 animate-pulse">
                                                <Loader2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                                                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Memuat Kartu...</span>
                                            </div>
                                        ) : cardImageUrl ? (
                                            <img
                                                src={cardImageUrl}
                                                alt="Kartu (Depan)"
                                                className="w-full h-auto object-contain rounded-2xl border border-slate-200/50 dark:border-slate-700/50 pointer-events-none shadow-sm"
                                            />
                                        ) : (
                                            <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 p-6 text-center">
                                                <ImageIcon className="w-10 h-10 opacity-40" />
                                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                                    Kartu (Depan) tidak dapat dimuat.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        onClick={handlePrintFront}
                                        disabled={!cardImageUrl || isLoadingCard}
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 rounded-2xl shadow-lg shadow-emerald-500/25 transition-all active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        <Printer className="w-4 h-4" />
                                        <span>Print Depan</span>
                                    </Button>
                                </TabsContent>

                                <TabsContent value="back" className="space-y-4 mt-0 border-0 p-0 focus-visible:ring-0">
                                    <div className="p-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-[2rem] shadow-xl">
                                        {isLoadingCard ? (
                                            <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 animate-pulse">
                                                <Loader2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                                                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Memuat Kartu...</span>
                                            </div>
                                        ) : cardBackImageUrl ? (
                                            <img
                                                src={cardBackImageUrl}
                                                alt="Kartu (Belakang)"
                                                className="w-full h-auto object-contain rounded-2xl border border-slate-200/50 dark:border-slate-700/50 pointer-events-none shadow-sm"
                                            />
                                        ) : (
                                            <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 p-6 text-center">
                                                <ImageIcon className="w-10 h-10 opacity-40" />
                                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                                    Kartu (Belakang) tidak dapat dimuat.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        onClick={handlePrintBack}
                                        disabled={!cardBackImageUrl || isLoadingCard}
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 rounded-2xl shadow-lg shadow-emerald-500/25 transition-all active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        <Printer className="w-4 h-4" />
                                        <span>Print Belakang</span>
                                    </Button>
                                </TabsContent>
                            </Tabs>
                        </div>

                        <div className="flex-1 min-w-0 w-full space-y-6">
                            <div className="space-y-4">
                                <h3 className="font-bold text-lg">Informasi Partisipan</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800">
                                        <span className="text-slate-500 flex items-center gap-2"><CreditCard className="w-4 h-4" /> No. Kartu</span>
                                        <span className="font-bold text-right font-mono">{guest.card_number}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Mail className="w-4 h-4" /> Email</span>
                                        <span className="font-bold text-right break-all">{guest.email || "-"}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Phone className="w-4 h-4" /> No. HP/WA</span>
                                        <span className="font-bold text-right">{guest.phone_mobile || "-"}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Building className="w-4 h-4" /> Institusi</span>
                                        <span className="font-bold text-right line-clamp-2">{guest.institution || "-"}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500">Jenis Kelamin</span>
                                        <span className="font-bold text-right">{genderLabel}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500">Jabatan</span>
                                        <span className="font-bold text-right">{guest.position || "-"}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500">Peran di Sertifikat</span>
                                        <span className="font-bold text-right">{guest.certificate_role || "-"}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500">Golongan Darah / Baju</span>
                                        <span className="font-bold text-right">{guest.blood_type || "-"} / {guest.clothing_size || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Nfc className="w-4 h-4" /> Status RFID</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`font-bold text-right text-xs px-2 py-0.5 rounded-md ${guest.rfid_number
                                                    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
                                                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
                                                }`}>
                                                {guest.rfid_number ? guest.rfid_number : "Belum Ada"}
                                            </span>
                                            {guest.rfid_number && (
                                                <Button
                                                    variant="destructive"
                                                    size="icon"
                                                    className="h-6 w-6 shrink-0"
                                                    onClick={handleDeleteRfidFromDetail}
                                                    disabled={deleteRfidMutation.isPending}
                                                    title="Hapus RFID"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Printer className="w-4 h-4" /> Status Cetak</span>
                                        <span className={`font-bold text-right text-xs px-2 py-0.5 rounded-md ${localIsPrinted
                                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                                : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                                            }`}>
                                            {localIsPrinted ? `Sudah Dicetak${guest.print_batch != null ? ` (Gel. ${guest.print_batch})` : ""}` : "Belum Dicetak"}
                                        </span>
                                    </div>
                                    {guest.notes && (
                                        <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                            <span className="text-slate-500">Catatan</span>
                                            <span className="font-bold text-right">{guest.notes}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Tindakan Tambahan */}
                            <div className="space-y-4 pt-4">
                                <h3 className="font-bold text-lg">Tindakan Cepat</h3>
                                <div className="space-y-3 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                            <Nfc className="w-3.5 h-3.5" />
                                            {guest.rfid_number ? "Perbarui / Ganti RFID" : "Daftarkan RFID"}
                                        </label>
                                        <form
                                            onSubmit={(e) => { e.preventDefault(); handleAssignRfidFromDetail(); }}
                                            className="flex gap-2"
                                        >
                                            <input
                                                type="text"
                                                autoFocus
                                                value={rfidCodeForModal}
                                                onChange={(e) => setRfidCodeForModal(e.target.value)}
                                                disabled={assignRfidMutation.isPending}
                                                placeholder="Scan/Ketik nomor RFID..."
                                                className="flex-1 h-10 bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-600 rounded-xl text-sm px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-bold text-slate-800 dark:text-slate-100 placeholder:text-slate-400 placeholder:font-normal transition-all"
                                            />
                                            <Button
                                                type="submit"
                                                disabled={assignRfidMutation.isPending || !rfidCodeForModal.trim()}
                                                className="h-10 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md shadow-indigo-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {assignRfidMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
                                            </Button>
                                        </form>
                                        {guest.rfid_number && (
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                                                RFID Terdaftar: <span className="font-mono text-slate-700 dark:text-slate-300">{guest.rfid_number}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-3 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                            <Printer className="w-3.5 h-3.5" />
                                            Ubah Status Cetak
                                        </label>
                                        <div className="flex gap-2 w-full">
                                            <Button
                                                onClick={handleToggleFromDetail}
                                                disabled={togglePrintedMutation.isPending}
                                                className={`flex-1 h-9 rounded-xl shadow-md transition-all active:scale-95 flex items-center justify-center gap-2 ${localIsPrinted
                                                        ? "bg-rose-600 hover:bg-rose-700 text-white shadow-rose-500/20"
                                                        : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-500/20"
                                                    }`}
                                            >
                                                {togglePrintedMutation.isPending ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : localIsPrinted ? (
                                                    <>
                                                        <XCircleIcon className="w-4 h-4" />
                                                        Batal Cetak
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        Tandai Cetak
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                                            Berguna jika ID Card dicetak secara fisik tanpa memindai secara otomatis.
                                        </p>
                                    </div>
                                </div>

                                {canEdit && (
                                    <Button onClick={() => onEdit(guest)} variant="outline" className="w-full">
                                        <Edit2 className="w-4 h-4 mr-2" /> Edit Data
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Konfirmasi Hapus RFID */}
                <AlertDialog open={isDeleteRfidDialogOpen} onOpenChange={setIsDeleteRfidDialogOpen}>
                    <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-md">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <Trash2 className="w-5 h-5 text-rose-500" />
                                Hapus/Lepas RFID
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                                Apakah Anda yakin ingin menghapus/melepas kartu RFID <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{guest?.rfid_number}</span> dari tamu ini?
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="mt-6 gap-2">
                            <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={confirmDeleteRfid}
                                disabled={deleteRfidMutation.isPending}
                                className="rounded-xl h-11 font-bold text-white shadow-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
                            >
                                {deleteRfidMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Ya, Hapus
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
        </Dialog>
    );
}

interface PrintQueueItem {
    source: "participant" | "guest";
    source_id: number;
    period_id: number;
    name: string;
    category: string;
    category_color: string;
    institution: string | null;
    card_number: string | null;
    rfid_number: string | null;
    is_printed: boolean;
    card_image_endpoint: string;
    rfid_assign_endpoint: string;
}

function PrintQueueTab({ periodId }: { periodId: string }) {
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [sourceFilter, setSourceFilter] = useState<"all" | "participant" | "guest">("all");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [rfidTarget, setRfidTarget] = useState<PrintQueueItem | null>(null);
    const [rfidValue, setRfidValue] = useState("");
    const [isAssigning, setIsAssigning] = useState(false);

    // Debounce pencarian supaya tidak request ke server setiap ketikan
    useEffect(() => {
        const timer = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const { data, isLoading, isFetching, refetch } = useQuery<{
        data: PrintQueueItem[];
        meta: { current_page: number; per_page: number; last_page: number; total: number; total_participants: number; total_guests: number; total_printed: number; total_with_rfid: number };
    }>({
        queryKey: ["print-queue", periodId, page, perPage, search, sourceFilter],
        queryFn: async () => {
            const { data } = await axiosClient.get("/print-queue", {
                params: { period_id: periodId, page, per_page: perPage, search, source: sourceFilter },
                withCredentials: true,
            });
            return data;
        },
        enabled: !!periodId,
    });

    const items = data?.data || [];
    const meta = data?.meta;

    const handlePrint = async (item: PrintQueueItem) => {
        try {
            const res = await axiosClient.get(item.card_image_endpoint, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "image/jpeg" }));
            window.open(url, "_blank");
        } catch {
            toast.error("Gagal memuat kartu");
        }
    };

    const handleAssignRfid = async () => {
        if (!rfidTarget || !rfidValue.trim()) return;
        setIsAssigning(true);
        try {
            await axiosClient.post(rfidTarget.rfid_assign_endpoint, { rfid_number: rfidValue.trim() });
            toast.success("RFID berhasil didaftarkan");
            setRfidTarget(null);
            setRfidValue("");
            refetch();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Gagal mendaftarkan RFID");
        } finally {
            setIsAssigning(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Total Antrean</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{meta?.total ?? 0}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Peserta</p>
                    <p className="text-3xl font-black text-sky-600">{meta?.total_participants ?? 0}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Panitia/Tamu</p>
                    <p className="text-3xl font-black text-indigo-600">{meta?.total_guests ?? 0}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Sudah Dicetak</p>
                    <p className="text-3xl font-black text-emerald-600">{meta?.total_printed ?? 0}</p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[250px]">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Cari nama atau institusi..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="w-full h-11 pl-11 pr-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm outline-none shadow-sm"
                    />
                </div>
                <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v as typeof sourceFilter); setPage(1); }}>
                    <SelectTrigger className="h-11 rounded-xl w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Semua Sumber</SelectItem>
                        <SelectItem value="participant">Peserta</SelectItem>
                        <SelectItem value="guest">Panitia/Tamu</SelectItem>
                    </SelectContent>
                </Select>
                {(searchInput || sourceFilter !== "all") && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                onClick={() => { setSearchInput(""); setSearch(""); setSourceFilter("all"); setPage(1); }}
                                className="h-11 rounded-xl"
                            >
                                Reset Filter
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top"><p>Kembalikan pencarian & filter sumber ke default</p></TooltipContent>
                    </Tooltip>
                )}
                {isFetching && !isLoading && (
                    <span className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Memuat...</span>
                )}
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
                    ) : (
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                                <tr>
                                    <th className="px-6 py-4">Nama</th>
                                    <th className="px-6 py-4">Kategori</th>
                                    <th className="px-6 py-4">Institusi</th>
                                    <th className="px-6 py-4">No. Kartu / RFID</th>
                                    <th className="px-6 py-4 text-center">Cetak</th>
                                    <th className="px-6 py-4 text-right">Aksi</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {items.length === 0 ? (
                                    <tr><td colSpan={6} className="p-12 text-center text-slate-500">Tidak ada data.</td></tr>
                                ) : items.map((item) => (
                                    <tr key={`${item.source}-${item.source_id}`} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20">
                                        <td className="px-6 py-3 font-semibold text-slate-900 dark:text-slate-100">{item.name}</td>
                                        <td className="px-6 py-3">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: item.category_color + "20", color: item.category_color }}>
                                                {item.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-slate-600 dark:text-slate-300">{item.institution || "-"}</td>
                                        <td className="px-6 py-3">
                                            <div className="font-mono text-xs">{item.card_number || "-"}</div>
                                            <button onClick={() => { setRfidTarget(item); setRfidValue(item.rfid_number || ""); }} className="text-[11px] mt-1 flex items-center gap-1 text-indigo-600 hover:underline">
                                                <Nfc className="w-3 h-3" /> {item.rfid_number || "Assign RFID"}
                                            </button>
                                        </td>
                                        <td className="px-6 py-3 text-center">
                                            {item.is_printed ? <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" /> : <XCircleIcon className="w-4 h-4 text-slate-300 inline" />}
                                        </td>
                                        <td className="px-6 py-3 text-right">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="outline" size="icon" className="h-8 w-8 text-slate-600" onClick={() => handlePrint(item)}>
                                                        <ImageIcon className="h-4 w-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top"><p>Cetak Kartu</p></TooltipContent>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                {meta && meta.total > 0 && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3 text-slate-500 font-medium">
                            <span>Menampilkan {items.length ? (meta.current_page - 1) * meta.per_page + 1 : 0} - {(meta.current_page - 1) * meta.per_page + items.length} dari {meta.total} data</span>
                            <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block" />
                            <div className="flex items-center gap-2">
                                <label htmlFor="printQueuePerPage">Tampilkan:</label>
                                <select
                                    id="printQueuePerPage"
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-primary font-bold"
                                >
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg">Sebelumnya</Button>
                            <span className="px-2 font-bold text-slate-700 dark:text-slate-300">{meta.current_page} / {meta.last_page}</span>
                            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))} disabled={page === meta.last_page} className="rounded-lg">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={!!rfidTarget} onOpenChange={(open) => !open && setRfidTarget(null)}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader><DialogTitle>Assign RFID — {rfidTarget?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-2">
                        <Input placeholder="Scan / ketik nomor RFID" value={rfidValue} onChange={(e) => setRfidValue(e.target.value)} />
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setRfidTarget(null)}>Batal</Button>
                            <Button onClick={handleAssignRfid} disabled={isAssigning} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                {isAssigning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
